/*
 * Named-modal compatibility for legacy gd-modal consumers. It deliberately
 * keeps the root event contract while delegating visible chrome and focus
 * semantics to the Vue 2 kit's GsModal.
 */

import createAccessibleModal from './accessible-modal';
import '../styles/named-modal-adapter.css';

const GsModal = createAccessibleModal();

export default {
  name: 'GsNamedModal',
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      required: true,
    },
    value: Boolean,
    width: {
      type: String,
      default: '',
    },
    maxWidth: {
      type: String,
      default: '',
    },
    height: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    bgType: {
      type: String,
      default: 'white',
    },
    noOverflow: {
      type: Boolean,
      default: false,
    },
    allowOverflow: {
      type: Boolean,
      default: false,
    },
    persist: {
      type: Boolean,
      default: true,
    },
    hideClose: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: this.value,
    };
  },
  watch: {
    value(value) {
      if (value) this.open();
      else this.close();
    },
  },
  mounted() {
    this.$root.$on('modal-open', this.openFromRoot);
    this.$root.$on('modal-close', this.closeFromRoot);
  },
  beforeDestroy() {
    this.$root.$off('modal-open', this.openFromRoot);
    this.$root.$off('modal-close', this.closeFromRoot);
  },
  methods: {
    openFromRoot(name) {
      if (name === this.name) this.open();
    },
    closeFromRoot(name) {
      if (name === this.name) this.close();
    },
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.$emit('input', true);
      this.$emit('opened');
    },
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.$emit('input', false);
      this.$emit('closed');
    },
  },
  render(h) {
    const footer = this.$slots.footer && this.$slots.footer.length
      ? h('div', { class: 'gs-named-modal__footer' }, this.$slots.footer)
      : null;
    const actions = this.$slots.actions && this.$slots.actions.length
      ? h('div', { class: 'gs-named-modal__actions' }, this.$slots.actions)
      : null;
    const style = {};

    if (this.width) style['--gs-named-modal-width'] = this.width;
    if (this.maxWidth) style['--gs-named-modal-max-width'] = this.maxWidth;
    if (this.height) style['--gs-named-modal-height'] = this.height;

    return h(
      GsModal,
      {
        class: [
          'gs-app',
          'gs-named-modal',
          this.name,
          `gs-named-modal--backdrop-${this.bgType}`,
          {
            'gs-named-modal--hide-close': this.hideClose,
            'gs-named-modal--allow-overflow': this.noOverflow || this.allowOverflow,
          },
        ],
        style,
        attrs: this.$attrs,
        props: {
          value: this.isOpen,
          title: this.title,
          size: 'xl',
          preventEscape: this.persist,
          hideHeader: this.hideHeader,
          closeOnBackdrop: !this.persist,
        },
        on: {
          input: value => {
            if (!value) this.close();
          },
        },
      },
      [actions, ...(this.$slots.default || []), footer]
    );
  },
};
